import { useCallback, useEffect, useState } from 'react';
import { apiRequest } from './api';

export type ApiState<T> =
  | { status: 'loading' }
  | { status: 'error'; error: Error }
  | { status: 'success'; data: T };

interface Result<T> {
  path: string;
  key: string;
  state: ApiState<T>;
}

/** Lädt `path` beim Mounten und bei Änderung. `reload()` lädt erneut. `path = null` lädt nichts. */
export function useApi<T>(path: string | null, token: string | null = null): [ApiState<T>, () => void] {
  const [result, setResult] = useState<Result<T> | null>(null);
  const [version, setVersion] = useState(0);
  const key = `${path ?? ''}|${token ?? ''}|${version}`;

  useEffect(() => {
    if (path === null) {
      return;
    }
    const controller = new AbortController();
    apiRequest<T>(path, { token, signal: controller.signal })
      .then((data) => setResult({ path, key, state: { status: 'success', data } }))
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setResult({ path, key, state: { status: 'error', error: error instanceof Error ? error : new Error(String(error)) } });
        }
      });
    return () => controller.abort();
  }, [path, token, key]);

  const reload = useCallback(() => setVersion((current) => current + 1), []);

  // Beim Neuladen derselben Ressource bleiben die alten Daten sichtbar (kein Flackern).
  const isCurrent = result?.key === key;
  const isSameResourceWithData = result?.path === path && result?.state.status === 'success';
  const state: ApiState<T> = result && (isCurrent || isSameResourceWithData) ? result.state : { status: 'loading' };
  return [state, reload];
}
